import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import StatusContainer from '@/components/StatusContainer';

export default function BotsPersonalSection({ hasUser, count, isLoading, error, onRetry, onCreate, children }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  const hasBots = hasUser && count > 0;

  return (
    <View style={styles.section} testID="bots-personal-section">
      <View style={styles.heading}>
        <View style={styles.title}>
          <Text tag="h2" size="xl" weight="bold">{t('pages.agents.myAgents')}</Text>
          {hasBots && <Text size="s" color="tertiary">{count}</Text>}
        </View>
      </View>
      {hasUser && error ? (
        <StatusContainer type="error" message={error} actionText={t('pages.common.retry')} onAction={onRetry} />
      ) : hasUser && (isLoading || hasBots) ? children : (
        <View style={styles.empty}>
          <View style={styles.emptyIntro}>
            <View style={styles.icon}><Icon name="robot" size={26} color={colors.text_color_primary} /></View>
            <View style={styles.emptyCopy}>
              <Text size="l" weight="bold">{t('pages.agents.intro.createTitle')}</Text>
              <Text size="n" color="secondary" style={styles.body}>{t('pages.agents.noAgentsYet')}</Text>
            </View>
          </View>
          <View style={styles.steps}>
            {[1, 2, 3].map((step) => (
              <View key={step} style={styles.step}>
                <View style={styles.stepNumber}><Text size="xs" weight="bold">{step}</Text></View>
                <Text size="s" color="secondary" style={styles.stepText}>{t(`pages.agents.intro.step${step}`)}</Text>
              </View>
            ))}
          </View>
          <View style={styles.emptyActions}>
            <Button text={t('pages.agents.intro.createCta')} prependIcon="plus" onPress={onCreate} />
            {!hasUser && <Text size="s" color="tertiary" style={styles.hint}>{t('pages.agents.intro.signInHint')}</Text>}
          </View>
        </View>
      )}
    </View>
  );
}

const createStyles = (colors, compact) => StyleSheet.create({
  section: { gap: 18 },
  heading: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  title: { flexDirection: 'row', alignItems: 'center', gap: 10, flexShrink: 1 },
  empty: { borderWidth: 1, borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block, borderRadius: 20, padding: compact ? 20 : 28, gap: 24 },
  emptyIntro: { flexDirection: 'row', alignItems: 'flex-start', gap: 16 },
  icon: { width: 52, height: 52, borderRadius: 16, backgroundColor: colors.solid_color_tertiary, alignItems: 'center', justifyContent: 'center' },
  emptyCopy: { flex: 1, minWidth: 0, gap: 6 },
  body: { lineHeight: 23, maxWidth: 620 },
  steps: { flexDirection: compact ? 'column' : 'row', gap: 16 },
  step: { flex: compact ? undefined : 1, flexDirection: 'row', alignItems: 'center', gap: 10 },
  stepNumber: { width: 26, height: 26, borderRadius: 13, backgroundColor: colors.solid_color_tertiary, alignItems: 'center', justifyContent: 'center' },
  stepText: { flex: 1, lineHeight: 21 },
  emptyActions: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 14 },
  hint: { flexShrink: 1 },
});
